import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Link,
  Divider,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNotify } from 'react-admin';
import { getToken } from '../authentication/authProvider';
import { supabase } from '../lib/supabaseClient';

const baseUrl = (process.env.REACT_APP_PAPERMAN_API_URL || '').replace(/\/$/, '');

/** Step-1 identity, as the bot will type it into the RRQ form. */
export interface RrqIdentityPayload {
  sin: string;
  familyName: string;
  givenName: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  phoneDay: string;
  phoneDayExt: string;
}

type Slot = 'application' | 'proof_of_payment';

interface Preview {
  funeralId: number;
  enabled: boolean;
  /** Every run is a dry run (local stack / RRQ_DIGITAL_SEND_DRY_RUN). */
  dryRun: boolean;
  payload: RrqIdentityPayload;
  /** Buyer = main contract's client, else the case's paying client. */
  applicant: { source: 'contract_client' | 'paying_client' | 'none'; contractId: number | null; clientId: number | null };
  files: Array<{ slot: Slot; filename: string; source: 'generated' | 'uploaded' }>;
  missing: string[];
  warnings: string[];
}

export interface RrqSubmission {
  id: string;
  mode: 'send' | 'dry_run';
  status: 'started' | 'sending' | 'sent' | 'dry_run_ok' | 'failed' | 'abandoned';
  error?: string | null;
  sent_at?: string | null;
  sent_by?: string | null;
  confirmation_number?: string | null;
  screenshot_path?: string | null;
}

interface Challenge {
  runId: string;
  captchaPngBase64: string;
  attempt: number;
  previousError?: string;
}

interface RunResult {
  submissionId: string;
  status: RrqSubmission['status'];
  mode: 'send' | 'dry_run';
  confirmationNumber?: string | null;
  screenshotBase64?: string;
  error?: string;
  message?: string;
  localScreenshotPath?: string | null;
  captcha?: Challenge;
}

type Replacement = { storagePath: string; filename: string; bytes: number };

const SLOT_LABEL: Record<Slot, string> = {
  application: 'QPP application',
  proof_of_payment: 'Proof of payment',
};

/** What the generated file is, for the "open" link. Same renders the bot uploads (flattened). */
const GENERATED_URL: Record<Slot, (id: number) => string> = {
  application: (id) => `${baseUrl}/survivor-benefits/form/${id}`,
  proof_of_payment: (id) => `${baseUrl}/proof-of-payment/${id}/flat`,
};

const EDITABLE: Array<{ key: keyof RrqIdentityPayload; label: string }> = [
  { key: 'familyName', label: 'Family name' },
  { key: 'givenName', label: 'Given name' },
  { key: 'address1', label: 'Address' },
  { key: 'address2', label: 'Address (line 2)' },
  { key: 'city', label: 'City' },
  { key: 'province', label: 'Province' },
  { key: 'country', label: 'Country' },
  { key: 'postalCode', label: 'Postal code' },
  { key: 'phoneDay', label: 'Daytime phone' },
];

const STEPS: Array<{ key: string; label: string }> = [
  { key: 'prepare', label: 'Prepare the documents' },
  { key: 'fill', label: 'Fill Identification' },
  { key: 'captcha', label: 'Security code' },
  { key: 'upload', label: 'Upload and virus-scan' },
  { key: 'transmit', label: 'Send to Retraite Québec' },
  { key: 'confirm', label: 'Save the confirmation' },
];

const maskSin = (sin: string) => (sin ? `*** *** ${sin.replace(/\D/g, '').slice(-3)}` : '—');

interface Props {
  open: boolean;
  onClose: () => void;
  funeralId: number;
  onSent?: () => void;
}

/**
 * Send a QC case's QPP application + proof of payment to Retraite Québec
 * through Envoi numérique (T-154). Modelled on BurialPermitRequestModal, with
 * one extra stage: the RRQ form has an image CAPTCHA, which the bot shows here
 * for a person to type.
 */
export default function RrqEnvoiModal({ open, onClose, funeralId, onSent }: Props) {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [submission, setSubmission] = useState<RrqSubmission | null>(null);
  const [edits, setEdits] = useState<Partial<RrqIdentityPayload>>({});
  const [replacements, setReplacements] = useState<Partial<Record<Slot, Replacement>>>({});
  const [staging, setStaging] = useState<Slot | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mode, setMode] = useState<'send' | 'dry_run'>('send');
  const [starting, setStarting] = useState(false);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [code, setCode] = useState('');
  // Staff confirm the information; the bot then ticks the RRQ's declaration
  // ("Je déclare que les renseignements fournis sont exacts") on their behalf.
  const [declared, setDeclared] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ step: string; detail?: string }>({ step: 'idle' });
  const [result, setResult] = useState<RunResult | null>(null);
  const fileInputs = useRef<Partial<Record<Slot, HTMLInputElement | null>>>({});

  const authedFetch = useCallback(async (path: string, init?: RequestInit) => {
    const token = await getToken();
    return fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers || {}),
      },
    });
  }, []);

  const loadPreview = useCallback(
    async (payload?: Partial<RrqIdentityPayload>, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = await authedFetch(`rrq-envoi/preview/${funeralId}`, {
          method: 'POST',
          body: JSON.stringify({ payload }),
        });
        if (!res.ok) throw new Error('Could not load the preview');
        const data = await res.json();
        setPreview(data.preview);
        setSubmission(data.latestSubmission);
      } catch (e: any) {
        if (!opts?.silent) notify(e.message || 'Could not load the preview', { type: 'error' });
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [authedFetch, funeralId, notify],
  );

  // Every open starts clean: no leftover edits, swaps or result from last time.
  useEffect(() => {
    if (!open) return;
    setEdits({});
    setDeclared(false);
    setReplacements({});
    setChallenge(null);
    setResult(null);
    setCode('');
  }, [open]);

  useEffect(() => {
    if (open) void loadPreview();
  }, [open, loadPreview]);

  // Re-validate server-side as staff edit, so the blocking list tracks the
  // values that will actually be typed.
  useEffect(() => {
    if (!open || !Object.keys(edits).length) return;
    const t = setTimeout(() => void loadPreview(edits, { silent: true }), 400);
    return () => clearTimeout(t);
  }, [edits, open, loadPreview]);

  // The service keeps progress in memory while a run is in flight.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const res = await authedFetch(`rrq-envoi/status/${funeralId}`);
          if (!res.ok) return;
          const data = await res.json();
          setProgress(data.progress || { step: 'idle' });
        } catch {
          /* transient — keep polling */
        }
      })();
    }, 1500);
    return () => clearInterval(timer);
  }, [running, authedFetch, funeralId]);

  const payload = preview ? { ...preview.payload, ...edits } : null;

  const handleReplace = async (slot: Slot, file: File) => {
    setStaging(slot);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1] || '');
        r.onerror = () => reject(new Error('Could not read the file'));
        r.readAsDataURL(file);
      });
      const res = await authedFetch(`rrq-envoi/stage-file/${funeralId}`, {
        method: 'POST',
        body: JSON.stringify({ slot, filename: file.name, base64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not use that file');
      setReplacements((r) => ({ ...r, [slot]: data }));
    } catch (e: any) {
      notify(e.message || 'Could not use that file', { type: 'error' });
    } finally {
      setStaging(null);
    }
  };

  const begin = async (runMode: 'send' | 'dry_run') => {
    setConfirmOpen(false);
    setMode(runMode);
    setStarting(true);
    setResult(null);
    setProgress({ step: 'prepare' });
    setRunning(true);
    try {
      const res = await authedFetch(`rrq-envoi/start/${funeralId}`, {
        method: 'POST',
        body: JSON.stringify({
          mode: runMode,
          payload: Object.keys(edits).length ? edits : undefined,
          declarationConfirmed: declared,
          replacements: Object.fromEntries(
            Object.entries(replacements).map(([slot, r]) => [slot, { storagePath: r!.storagePath, filename: r!.filename }]),
          ),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not open the RRQ form');
      setChallenge(data);
      setCode('');
    } catch (e: any) {
      notify(e.message || 'Could not open the RRQ form', { type: 'error' });
      setRunning(false);
      void loadPreview(edits, { silent: true });
    } finally {
      setStarting(false);
    }
  };

  const answer = async () => {
    if (!challenge) return;
    const runId = challenge.runId;
    setChallenge(null);
    try {
      const res = await authedFetch(`rrq-envoi/captcha/${runId}`, {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      const data: RunResult & { message?: string } = await res.json();
      if (!res.ok) throw new Error(data.message || 'The run ended');
      if (data.captcha) {
        setChallenge(data.captcha);
        setCode('');
        return;
      }
      setResult(data);
      setRunning(false);
      if (data.status === 'sent') {
        notify('Sent to Retraite Québec', { type: 'success' });
        onSent?.();
      } else if (data.status === 'dry_run_ok') {
        notify('Dry run complete — nothing was sent', { type: 'success' });
      } else {
        notify(data.error || 'Sending failed', { type: 'error' });
      }
      void loadPreview(edits, { silent: true });
    } catch (e: any) {
      notify(e.message || 'Sending failed', { type: 'error' });
      setRunning(false);
      void loadPreview(edits, { silent: true });
    }
  };

  const refreshCaptcha = async () => {
    if (!challenge) return;
    const res = await authedFetch(`rrq-envoi/captcha/${challenge.runId}/refresh`, { method: 'POST' });
    if (res.ok) setChallenge(await res.json());
  };

  const cancelRun = async () => {
    if (challenge) {
      await authedFetch(`rrq-envoi/cancel/${challenge.runId}`, { method: 'POST' }).catch(() => undefined);
    }
    setChallenge(null);
    setRunning(false);
    void loadPreview(edits, { silent: true });
  };

  const blocked = !preview || !!preview.missing.length || !preview.enabled;
  const busy = loading || running || starting || !!staging;
  const lastSent = submission?.status === 'sent' ? submission : null;
  const uncertain = submission?.status === 'failed' && /WAS clicked/.test(submission.error || '');

  const row = (label: string, value: React.ReactNode) => (
    <TableRow key={label}>
      <TableCell sx={{ width: 170, color: 'text.secondary', border: 0, py: 0.5 }}>{label}</TableCell>
      <TableCell sx={{ border: 0, py: 0.5, fontWeight: 500 }}>{value}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send to Retraite Québec</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && preview && payload && !challenge && !result && (
          <>
            {preview.dryRun ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                <AlertTitle>Dry run</AlertTitle>
                Fills Identification, uploads two TEST placeholder PDFs, and stops before
                Transmettre. Nothing is sent to Retraite Québec and nothing is saved to the case.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This is exactly what will be sent through Envoi numérique.
              </Typography>
            )}

            <Typography variant="overline" color="text.secondary">Deceased's file</Typography>
            <Table size="small">
              <TableBody>
                {preview.dryRun
                  ? row(
                      'SIN (made-up)',
                      <TextField
                        size="small"
                        variant="standard"
                        fullWidth
                        value={payload.sin}
                        disabled={busy}
                        onChange={(e) => setEdits((cur) => ({ ...cur, sin: e.target.value.replace(/\D/g, '') }))}
                        inputProps={{ 'aria-label': 'SIN' }}
                      />,
                    )
                  : row('SIN', maskSin(payload.sin))}
              </TableBody>
            </Table>

            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Applicant (buyer)
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {preview.applicant.source === 'contract_client'
                ? `From contract #${preview.applicant.contractId}'s client (client #${preview.applicant.clientId}).`
                : preview.applicant.source === 'paying_client'
                  ? `No contract client — from the case's paying client (client #${preview.applicant.clientId}).`
                  : 'No contract client and no paying client — type the buyer below.'}{' '}
              Edit anything here; the case itself isn't changed.
            </Typography>
            <Table size="small">
              <TableBody>
                {EDITABLE.map(({ key, label }) =>
                  row(
                    label,
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      value={payload[key] || ''}
                      disabled={busy}
                      onChange={(e) => setEdits((cur) => ({ ...cur, [key]: e.target.value }))}
                      inputProps={{ 'aria-label': label }}
                    />,
                  ),
                )}
              </TableBody>
            </Table>

            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Files
            </Typography>
            <Table size="small">
              <TableBody>
                {preview.files.map((f) => {
                  const swap = replacements[f.slot];
                  return row(
                    SLOT_LABEL[f.slot],
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {preview.dryRun ? (
                        <span>
                          {f.filename}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            (TEST placeholder)
                          </Typography>
                        </span>
                      ) : swap ? (
                        <span>
                          {swap.filename}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({(swap.bytes / 1048576).toFixed(1)} MB, replaced)
                          </Typography>
                        </span>
                      ) : (
                        <Link
                          href={GENERATED_URL[f.slot](funeralId)}
                          target="_blank"
                          rel="noopener"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {f.filename}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      )}
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                        ref={(el) => { fileInputs.current[f.slot] = el; }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) void handleReplace(f.slot, file);
                        }}
                      />
                      {!preview.dryRun && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => fileInputs.current[f.slot]?.click()}
                        sx={{ minWidth: 0, py: 0 }}
                      >
                        {staging === f.slot ? 'Checking…' : 'Replace'}
                      </Button>
                      )}
                      {swap && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => setReplacements((r) => ({ ...r, [f.slot]: undefined }))}
                          sx={{ minWidth: 0, py: 0 }}
                        >
                          Use generated
                        </Button>
                      )}
                    </Box>,
                  );
                })}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              PDF or JPG, 7 MB each at most.
            </Typography>

            <FormControlLabel
              sx={{ mt: 1.5, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={declared}
                  onChange={(e) => setDeclared(e.target.checked)}
                  disabled={busy}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Typography variant="body2">
                  I confirm the information is accurate{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    (ticks the RRQ's declaration « Je déclare que les renseignements fournis sont
                    exacts » on my behalf, along with the « Régime de rentes du Québec » program box)
                  </Typography>
                </Typography>
              }
            />

            {!preview.enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Sending to Retraite Québec is turned off (feature flag rrq_digital_send_enabled).
              </Alert>
            )}

            {preview.missing.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Not ready to send</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {!preview.missing.length && preview.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {preview.warnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </Alert>
            )}

            {uncertain && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>The last send may have reached the RRQ</AlertTitle>
                <Typography variant="body2">{submission?.error}</Typography>
                <ScreenshotLink submissionId={submission!.id} authedFetch={authedFetch} />
              </Alert>
            )}

            {submission?.status === 'failed' && !uncertain && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>The last attempt failed — nothing was sent</AlertTitle>
                <Typography variant="body2">{submission.error}</Typography>
                <ScreenshotLink submissionId={submission.id} authedFetch={authedFetch} />
              </Alert>
            )}

            {lastSent && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Sent {lastSent.sent_at ? new Date(lastSent.sent_at).toLocaleString('en-CA') : ''}
                {lastSent.sent_by ? ` by ${lastSent.sent_by}` : ''}
                {lastSent.confirmation_number ? ` — confirmation ${lastSent.confirmation_number}` : ''}.
                <ScreenshotLink submissionId={lastSent.id} authedFetch={authedFetch} />
              </Alert>
            )}
          </>
        )}

        {challenge && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Type the characters in the image. The RRQ asks for this before every send; the bot
              can't read it.
            </Typography>
            {challenge.previousError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                That code was not accepted ({challenge.previousError}). Here is a new one.
              </Alert>
            )}
            <Box display="flex" alignItems="center" gap={1} my={1}>
              <Box
                component="img"
                src={`data:image/png;base64,${challenge.captchaPngBase64}`}
                alt="Security code"
                sx={{ height: 50, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              />
              <Tooltip title="New image">
                <IconButton size="small" onClick={() => void refreshCaptcha()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              autoFocus
              size="small"
              label="Security code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.trim()) void answer();
              }}
              inputProps={{ autoComplete: 'off', spellCheck: false }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {mode === 'send'
                ? 'Continuing uploads both files and sends them to Retraite Québec.'
                : 'Test run: uploads both files and stops before Transmettre. Nothing is sent.'}
            </Typography>
          </Box>
        )}

        {running && !challenge && (
          <Box mt={1}>
            {STEPS.filter((s) => mode === 'send' || (s.key !== 'transmit' && s.key !== 'confirm')).map((s) => {
              const currentIndex = STEPS.findIndex((x) => x.key === progress.step);
              const thisIndex = STEPS.findIndex((x) => x.key === s.key);
              const done = currentIndex > thisIndex;
              const active = currentIndex === thisIndex;
              return (
                <Box key={s.key} display="flex" alignItems="center" gap={1} py={0.5}>
                  {active ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: done ? 'success.main' : 'action.disabledBackground',
                      }}
                    />
                  )}
                  <Typography variant="body2" color={active || done ? 'text.primary' : 'text.secondary'}>
                    {s.label}
                  </Typography>
                </Box>
              );
            })}
            {progress.detail && (
              <Typography variant="caption" color="text.secondary">
                {progress.detail}
              </Typography>
            )}
          </Box>
        )}

        {result && (
          <>
            {result.status === 'sent' && (
              <Alert severity="success">
                <AlertTitle>Sent to Retraite Québec</AlertTitle>
                {result.confirmationNumber
                  ? `Confirmation ${result.confirmationNumber}. `
                  : 'Confirmation number not read automatically yet — it is on the screenshot below. '}
                The screenshot is saved in Documents.
              </Alert>
            )}
            {result.status === 'dry_run_ok' && (
              <Alert severity="success">
                <AlertTitle>Dry run complete — nothing was sent</AlertTitle>
                Both files were accepted and the bot stopped before Transmettre.
                {result.localScreenshotPath && (
                  <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                    Screenshot saved on the server at {result.localScreenshotPath}
                  </Typography>
                )}
              </Alert>
            )}
            {result.status === 'failed' && (
              <Alert severity="error">
                <AlertTitle>
                  {/WAS clicked/.test(result.error || '')
                    ? 'Something went wrong after Transmettre — the RRQ may have it'
                    : 'Not sent'}
                </AlertTitle>
                {result.error}
              </Alert>
            )}
            {result.screenshotBase64 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  component="img"
                  src={`data:image/png;base64,${result.screenshotBase64}`}
                  alt="Retraite Québec page"
                  sx={{ width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {challenge ? (
          <>
            <Button onClick={() => void cancelRun()}>Cancel</Button>
            <Button variant="contained" disabled={!code.trim()} onClick={() => void answer()}>
              Continue
            </Button>
          </>
        ) : result ? (
          <>
            <Button onClick={() => setResult(null)}>Back</Button>
            <Button variant="contained" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
            <Button
              variant={preview?.dryRun ? 'contained' : 'text'}
              onClick={() => void begin('dry_run')}
              disabled={busy || blocked || !declared}
            >
              {starting && mode === 'dry_run' ? 'Opening…' : preview?.dryRun ? 'Start dry run' : 'Test run'}
            </Button>
            {!preview?.dryRun && (
              <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={busy || blocked || !declared}>
                {starting && mode === 'send' ? 'Opening…' : 'Send to Retraite Québec'}
              </Button>
            )}
          </>
        )}
      </DialogActions>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Send to Retraite Québec?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This files the QPP application and proof of payment in the deceased's RRQ file. It
            can't be undone{lastSent ? ', and this case was already sent once' : ''}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void begin('send')}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

/** Opens a run's screenshot from the private bucket, via the backend. */
function ScreenshotLink({
  submissionId,
  authedFetch,
}: {
  submissionId: string;
  authedFetch: (path: string, init?: RequestInit) => Promise<Response>;
}) {
  return (
    <Link
      component="button"
      variant="body2"
      sx={{ display: 'block', mt: 0.5 }}
      onClick={() => {
        void (async () => {
          const res = await authedFetch(`rrq-envoi/screenshot/${submissionId}`);
          if (res.ok) window.open(URL.createObjectURL(await res.blob()), '_blank');
        })();
      }}
    >
      View screenshot
    </Link>
  );
}

/** Latest RRQ attempt for the sidebar badge. Authenticated users can read the table. */
export function useLatestRrqSubmission(funeralId: number | undefined, reloadKey = 0) {
  const [row, setRow] = useState<RrqSubmission | null>(null);
  useEffect(() => {
    if (!funeralId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('rrq_envoi_submissions')
        .select('id, mode, status, error, sent_at, sent_by, confirmation_number')
        .eq('funeral_id', funeralId)
        .eq('mode', 'send')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setRow((data as RrqSubmission) || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [funeralId, reloadKey]);
  return row;
}

/** Sidebar row under the QPP forms: label opens the modal, badge shows the last send. */
export function RrqEnvoiRow({ funeralId, onSent }: { funeralId: number; onSent?: () => void }) {
  const [open, setOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const latest = useLatestRrqSubmission(funeralId, reloadKey);
  const badge =
    latest?.status === 'sent'
      ? { label: 'Sent', cls: 'bg-green-100 text-green-800' }
      : latest?.status === 'failed'
        ? { label: 'Failed', cls: 'bg-red-100 text-red-800' }
        : latest?.status === 'sending' || latest?.status === 'started'
          ? { label: 'Sending', cls: 'bg-blue-100 text-blue-800' }
          : { label: 'Not sent', cls: 'bg-gray-100 text-gray-600' };
  const title =
    latest?.status === 'sent' && latest.sent_at
      ? `Sent ${new Date(latest.sent_at).toLocaleString('en-CA')}${latest.confirmation_number ? ` · ${latest.confirmation_number}` : ''}`
      : latest?.status === 'failed'
        ? latest.error || undefined
        : undefined;
  return (
    <>
      <div className="flex justify-between items-center py-1.5">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          style={{ color: '#2563eb' }}
          className="text-[13px] hover:underline leading-5 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring rounded text-left font-medium"
        >
          Send to RRQ
        </button>
        <button
          type="button"
          title={title}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="inline-flex items-center cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
        </button>
      </div>
      {open && (
        <RrqEnvoiModal
          open={open}
          onClose={() => {
            setOpen(false);
            setReloadKey((k) => k + 1);
          }}
          funeralId={funeralId}
          onSent={() => {
            setReloadKey((k) => k + 1);
            onSent?.();
          }}
        />
      )}
    </>
  );
}
